import React from 'react'
import styled from 'styled-components'
import { CheckCircle2 } from 'lucide-react'
import {
  colors,
  spacing,
  mediaQuery,
  borderRadius,
  typographyMixin,
  heading,
  body,
  fontWeight,
} from 'src/constants/marketing-ds'
import XLogo from './XLogo'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { useSelector } from 'react-redux'
import { selectStateForProtobuf } from 'src/modules/reducers'

const HeroSection = ({ onScrollToFacts, tweetText, onTweetClick }) => {
  const stateForProtobuf = useSelector(selectStateForProtobuf)

  const handleTweetClick = () => {
    onTweetClick()
    fireUIInteractionEvent(
      {
        element: 'Post to X',
        component: 'hero_post_to_x_clicked',
        type: 'clicked',
      },
      stateForProtobuf
    )
  }

  const handleReadFactsClick = () => {
    onScrollToFacts()
    fireUIInteractionEvent(
      {
        element: 'Read The Facts',
        component: 'hero_read_facts_clicked',
        type: 'clicked',
      },
      stateForProtobuf
    )
  }

  return (
    <Hero>
      <HeroBackground>
        <BackgroundBlob $position="top-right" $color="orange" />
        <BackgroundBlob $position="bottom-left" $color="blue" />
      </HeroBackground>
      <HeroContent>
        <StatusBadge>
          <StatusDot />
          <StatusText>Status: Ongoing Negotiations</StatusText>
        </StatusBadge>
        <HeroTitle>
          NBCU wants you to pay for programming{' '}
          <HighlightedText>
            <HighlightedTextInner>you don’t watch.</HighlightedTextInner>
            <HighlightUnderline />
          </HighlightedText>
          <br />
          We're drawing the line.
        </HeroTitle>
        <HeroDescription>
          NBCU is shifting content to their own streaming services while forcing Fubo to carry
          channels you don’t want at expensive rates. They are blocking your ability to choose
          cheaper alternatives.
        </HeroDescription>
        <HeroActions>
          <CreditBadge>
            <CheckCircle2 size={20} />
            $15 Credit Auto-Applied*
          </CreditBadge>
          <ButtonRow>
            <TweetButton
              href={`https://twitter.com/intent/tweet?text=${tweetText}`}
              target="_blank"
              rel="noreferrer"
              onClick={handleTweetClick}
            >
              <ButtonContent>
                <XLogo size={24} />
                Post to X
              </ButtonContent>
            </TweetButton>
            <ReadFactsButton onClick={handleReadFactsClick}>Read The Facts</ReadFactsButton>
          </ButtonRow>
        </HeroActions>
        <CreditNote>
          *No action required. Credit applied to your first bill on or after Dec. 1, 2025, if the
          blackout continues.
        </CreditNote>
      </HeroContent>
    </Hero>
  )
}

export default HeroSection

const Hero = styled.header`
  position: relative;
  padding: ${spacing.xl} ${spacing.sm};
  overflow: hidden;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.slate[160]};
  ${mediaQuery.tablet} {
    padding: ${spacing.xxxl} ${spacing.md};
  }
`

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: 100%;
  max-width: 1280px;
  pointer-events: none;
`

const BackgroundBlob = styled.div`
  position: absolute;
  width: ${(props) => (props.$position === 'top-right' ? '600px' : '500px')};
  height: ${(props) => (props.$position === 'top-right' ? '600px' : '500px')};
  border-radius: 50%;
  filter: blur(100px);
  background-color: ${(props) =>
    props.$color === 'orange' ? 'rgba(255, 154, 0, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
  ${(props) =>
    props.$position === 'top-right'
      ? `
    top: -200px;
    right: -100px;
  `
      : `
    bottom: 0;
    left: -100px;
  `}
`

const HeroContent = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 10;
`

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xxxs};
  padding: ${spacing.xxxs} ${spacing.sm};
  border-radius: 9999px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  margin-bottom: ${spacing.lg};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  background-color: #dc2626;
  border-radius: 50%;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`

const StatusText = styled.span`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`

const HeroTitle = styled.h1`
  ${typographyMixin(heading.lg)}
  letter-spacing: -0.02em;
  margin-bottom: ${spacing.lg};
  line-height: 1.1;
  color: ${colors.slate[60]};

  ${mediaQuery.tablet} {
    font-size: 60px;
  }
  ${mediaQuery.desktop} {
    font-size: 72px;
  }
  font-weight: 800;
`

const HighlightedText = styled.span`
  position: relative;
  display: inline;
  white-space: nowrap;
  color: ${colors.slate[60]};
`

const HighlightedTextInner = styled.span`
  position: relative;
  z-index: 10;
`

const HighlightUnderline = styled.span`
  position: absolute;
  bottom: 4px;
  left: 0;
  width: 100%;
  height: 8px;

  background-color: rgba(253, 186, 116, 0.9);
  transform: skewX(-12deg);
  z-index: 0;
  mix-blend-mode: multiply;
  ${mediaQuery.tablet} {
    height: 16px;
  }
`

const HeroDescription = styled.p`
  ${typographyMixin(body.sm, { fontWeight: fontWeight.normal })}
  color: ${colors.slate[60]};
  margin-bottom: ${spacing.xl};
  max-width: 890px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  ${mediaQuery.tablet} {
    ${typographyMixin(body.md, { fontWeight: fontWeight.normal })}
  }

  ${mediaQuery.desktop} {
    ${typographyMixin(body.lg, { fontWeight: fontWeight.normal })}
  }
`

const HeroActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.md};
`

const CreditBadge = styled.div`
  padding: ${spacing.sm} ${spacing.lg};
  background-color: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
  ${typographyMixin(body.md)}
  font-weight: 700;

  border-radius: ${borderRadius.md};
  display: flex;
  align-items: center;
  gap: ${spacing.xxxs};
  cursor: default;
`

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm};
  width: 100%;
  max-width: 500px;

  ${mediaQuery.mobileMax} {
    flex-direction: row;
  }
`

const TweetButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xxxs};
  background-color: ${colors.slate[30]};
  color: ${colors.white};
  ${typographyMixin(body.md)}
  font-weight: 700;
  padding: ${spacing.sm} ${spacing.lg};
  border-radius: ${borderRadius.md};
  transition: all 0.2s;
  text-decoration: none;
  width: 100%;
  border: none;
  box-sizing: border-box;

  ${mediaQuery.mobileMax} {
    width: auto;
    flex: 1;
  }

  &:hover {
    background-color: ${colors.slate[60]};
  }

  svg {
    color: ${colors.white};
  }
`

const ButtonContent = styled.span`
  display: flex;
  align-items: center;
  gap: ${spacing.xxxs};
`

const ReadFactsButton = styled.button`
  padding: ${spacing.sm} ${spacing.lg};
  border: 1px solid ${colors.slate[160]};
  background-color: ${colors.white};
  color: ${colors.slate[60]};
  ${typographyMixin(body.md)}
  font-weight: 700;
  border-radius: ${borderRadius.md};
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  box-sizing: border-box;

  ${mediaQuery.mobileMax} {
    width: auto;
    flex: 1;
  }

  &:hover {
    background-color: ${colors.slate[190]};
    border-color: ${colors.slate[100]};
  }
`

const CreditNote = styled.p`
  margin-top: ${spacing.sm};
  font-size: 14px;
  color: ${colors.slate[100]};
`
